import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  COMMISSION_BASIS_MAP,
  COMMISSION_TARGET_POLICY_MAP,
  COMMISSION_TARGET_SOURCE_MAP,
  PAYROLL_RUN_STATUS_MAP,
  type CommissionBasis,
  type CommissionTargetPolicy,
  type CommissionTargetSource,
  type PayrollRunStatus,
} from '@goyone/shared';
import { api, qs } from '../../lib/api';
import { useAuth } from '../../auth/AuthProvider';
import { useTenantProfile } from '../../lib/settings';
import { WORDS, date, money, num, plural } from '../../lib/format';
import { Icon } from '../../components/Icon';
import { DocPreview } from '../../components/DocPreview';
import {
  AchieveRing,
  Fact,
  FactGrid,
  MoneyTile,
  YearSwitch,
} from '../../components/staff';
import {
  Badge,
  EmptyState,
  ErrorState,
  Modal,
  Notice,
  PageHead,
  Stat,
  TableSkeleton,
} from '../../components/ui';

interface Run {
  id: string;
  period: string;
  periodLabel: string;
  status: PayrollRunStatus;
  basis: CommissionBasis;
  basisLabel: string;
  targetPolicy: CommissionTargetPolicy;
  targetPolicyLabel: string;
  defaultRate: number;
  total: number;
  repCount: number;
  notes: string | null;
}

interface Line {
  id: string;
  employeeId: string;
  employeeName: string;
  employeeCode: string | null;
  jobTitle: string | null;
  basisAmount: number;
  target: number | null;
  /** من أين جاء الهدف — null في كشوفٍ سبقت الخطة المفترة. */
  targetSource: CommissionTargetSource | null;
  targetSourceLabel: string | null;
  rate: number;
  eligible: number;
  commission: number;
  documentCount: number;
  achievement: number | null;
  blockedReason: string | null;
  paid: boolean;
}

interface RunDetail extends Run {
  lines: Line[];
  totals: {
    invoiced: number;
    invoiceCount: number;
    collected: number;
    paymentCount: number;
  };
}

/** أقرب شهر مكتمل — الشهر الجاري لم تُحصَّل كل فواتيره بعد. */
function lastMonth(): string {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1))
    .toISOString()
    .slice(0, 7);
}

/** شريط تحقيق الهدف. يُقصّ عند مئة وخمسين فلا يبتلع مبالغٌ استثنائية الجدول. */
function Achievement({ value }: { value: number | null }) {
  if (value == null) return <span className="cell-mute">—</span>;

  const width = Math.min(150, value);
  const kind = value >= 100 ? 'ok' : value >= 70 ? 'warn' : 'low';

  return (
    <div className="achieve" title={`${num(value)} بالمئة من الهدف`}>
      <div className="achieve-bar">
        <span
          className={`achieve-fill achieve-${kind}`}
          style={{ width: `${width / 1.5}%` }}
        />
      </div>
      <span className="achieve-num num">{num(value)}%</span>
    </div>
  );
}

/* ==========================================================================
   قائمة الكشوف
   ========================================================================== */

/**
 * كشوفُ العمولات — سنةً سنة.
 *
 * الكشفُ شهريّ، وقراءتُه سنويّة: مع المرتّبات ومع الضريبة ومع تقويم
 * الشركة. فالقائمةُ تُصفّى بالسنة، وبطاقاتُها تقول حالَ تلك السنة وحدَها —
 * مجموعٌ يخلط سنتين لا يُتصرَّف فيه.
 */
export function CommissionRuns() {
  const { atLeast } = useAuth();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [creating, setCreating] = useState(false);
  const canManage = atLeast('ADMIN');

  const runs = useQuery({
    queryKey: ['commissions', 'runs', year],
    queryFn: () => api<Run[]>(`/commissions/runs${qs({ year })}`),
    placeholderData: (prev) => prev,
  });

  const rows = runs.data ?? [];
  const sum = (rs: Run[]) => rs.reduce((n, r) => n + r.total, 0);
  const paid = rows.filter((r) => r.status === 'PAID');
  const approved = rows.filter((r) => r.status === 'APPROVED');
  const drafts = rows.filter((r) => r.status === 'DRAFT');
  const total = sum(rows);

  return (
    <div className="page">
      <PageHead
        title="العمولات"
        subtitle="كشوف عمولات المبيعات الشهرية"
        actions={
          <>
            <YearSwitch year={year} onYear={setYear} max={now.getFullYear() + 1} />
            {canManage && (
              <>
                {/* القواعد كلها في الإعدادات — وهذا بابها لا نسخةٌ ثانية منها. */}
                <Link to="/settings/commissions" className="btn">
                  <Icon name="settings" size={15} />
                  قواعد الاحتساب
                </Link>
                <button
                  className="btn btn-primary"
                  onClick={() => setCreating(true)}
                >
                  كشف جديد
                </button>
              </>
            )}
          </>
        }
      />

      {runs.isError && (
        <ErrorState error={runs.error} onRetry={() => runs.refetch()} />
      )}

      {drafts.length > 0 && (
        <div className="notices">
          <Notice
            kind="warning"
            icon="clock"
            title={`${plural(drafts.length, WORDS.run)} في المسوّدة`}
          >
            كشفٌ عالقٌ في المسوّدة لا يصل المندوب ولا ينزل مع مرتّبه. افتحه
            وأتمّ خطواتِ اعتماده.
          </Notice>
        </div>
      )}

      <div className="cz mb-4">
        <MoneyTile
          tone="var(--brand)"
          icon="wallet"
          label={`عمولات ${year}`}
          value={money(total)}
          share={total > 0 ? Math.round((sum(paid) / total) * 100) : 0}
          note={plural(rows.length, WORDS.run)}
        />
        <MoneyTile
          tone="var(--success)"
          icon="check"
          label="مصروفة"
          value={money(sum(paid))}
          share={total > 0 ? Math.round((sum(paid) / total) * 100) : 0}
          note={plural(paid.length, WORDS.run)}
        />
        <MoneyTile
          tone="var(--warning)"
          icon="clock"
          label="معتمدة تنتظر الصرف"
          value={money(sum(approved))}
          share={total > 0 ? Math.round((sum(approved) / total) * 100) : 0}
          note={
            approved.length ? plural(approved.length, WORDS.run) : 'لا شيء معلّق'
          }
        />
        <MoneyTile
          tone="var(--mv-3)"
          icon="edit"
          label="مسوّدات"
          value={money(sum(drafts))}
          share={total > 0 ? Math.round((sum(drafts) / total) * 100) : 0}
          note={drafts.length ? 'لم تُعتمد بعد' : 'لا مسوّدات'}
        />
      </div>

      <div className="card">
        <div className="table-wrap sticky-head">
          <table className="table">
            <thead>
              <tr>
                <th>الشهر</th>
                <th>الحالة</th>
                <th>الأساس</th>
                <th>سياسة الهدف</th>
                <th className="col-num">المندوبون</th>
                <th className="col-num">إجمالي العمولات</th>
                <th className="col-act" />
              </tr>
            </thead>
            {runs.isLoading && <TableSkeleton rows={4} cols={7} />}

            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>
                    <Link
                      to={`/sales/commissions/${r.id}`}
                      className="cell-strong link"
                    >
                      {r.periodLabel}
                    </Link>
                  </td>
                  <td>
                    <Badge kind={PAYROLL_RUN_STATUS_MAP[r.status].kind}>
                      {PAYROLL_RUN_STATUS_MAP[r.status].label}
                    </Badge>
                  </td>
                  <td className="cell-mute">{r.basisLabel}</td>
                  <td className="cell-mute">{r.targetPolicyLabel}</td>
                  <td className="num cell-mute">{r.repCount}</td>
                  <td className="num cell-strong">{money(r.total)}</td>
                  <td className="col-act">
                    <Link
                      to={`/sales/commissions/${r.id}`}
                      className="btn btn-sm"
                    >
                      {r.status === 'DRAFT' ? 'أتمّ الاعتماد' : 'فتح'}
                      <Icon name="chevronLeft" size={13} />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!runs.isLoading && rows.length === 0 && (
          <EmptyState
            title={`لا كشوف عمولات في ${year}`}
            text="افتح كشفًا لشهر مضى لتُحسب عمولات المندوبين من فواتيرهم وتحصيلاتهم."
          />
        )}
      </div>

      {creating && <CreateRunDialog onClose={() => setCreating(false)} />}
    </div>
  );
}

/**
 * إنشاء كشفٍ لشهر.
 *
 * لا قواعد تُضبط هنا: مصدرها الإعدادات وحدها كي لا يوجد موضعان يقولان في
 * النسبة قولين. وما هو ساري يُعرض للقراءة قبل الحساب، فلا يُنشئ المدير كشفًا
 * وهو لا يدري بأيّ أساسٍ سيُحسب.
 */
function CreateRunDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const profile = useTenantProfile();
  const [period, setPeriod] = useState(lastMonth());

  const settings = profile.data?.settings;
  const basis = (settings?.commissionBasis as CommissionBasis) ?? 'COLLECTED';
  const policy =
    (settings?.commissionTargetPolicy as CommissionTargetPolicy) ?? 'NONE';
  const rate = settings?.commissionDefaultRate ?? 0;

  const create = useMutation({
    mutationFn: () =>
      api<Run>('/commissions/runs', {
        method: 'POST',
        body: { period },
      }),
    onSuccess: (run) => {
      void queryClient.invalidateQueries({ queryKey: ['commissions'] });
      window.location.href = `/sales/commissions/${run.id}`;
    },
  });

  return (
    <Modal
      title="كشف عمولات جديد"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => create.mutate()}
            disabled={create.isPending}
          >
            {create.isPending ? 'جارٍ الحساب' : 'إنشاء وحساب'}
          </button>
        </>
      }
    >
      {create.isError && (
        <div className="alert alert-danger mb-4">
          {create.error instanceof Error
            ? create.error.message
            : 'تعذّر الإنشاء.'}
        </div>
      )}

      <div className="field">
        <label className="label req" htmlFor="c-period">
          شهر العمولة
        </label>
        <input
          id="c-period"
          className="input num"
          type="month"
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
        />
      </div>

      {/* القاعدة السارية — تُقرأ ولا تُعدَّل من هنا. */}
      <div className="stats mt-4">
        <Stat label="أساس الاحتساب" value={COMMISSION_BASIS_MAP[basis].label} />
        <Stat
          label="سياسة الهدف"
          value={COMMISSION_TARGET_POLICY_MAP[policy].label}
        />
        <Stat label="النسبة الافتراضية" value={`${num(rate)}%`} />
      </div>

      <p className="tiny mute mt-2">
        لتغييرها: <Link to="/settings/commissions">إعدادات العمولات</Link>.
      </p>

      {/*
        القواعد تُنسخ في الكشف لحظة إنشائه.
        تعديل النسبة في الإعدادات بعد شهور يجب ألّا يغيّر عمولةً أُقرَّت
        وصُرفت، وإلا اختلف ما في النظام عمّا قبضه المندوب.
      */}
      <div className="alert alert-info mt-4">
        تُحسب عمولة كل مندوب من فواتيره في هذا الشهر، وتُجمَّد القواعد داخل
        الكشف. تعديل النسب أو الأهداف بعد ذلك لا يغيّر كشفًا محسوبًا.
      </div>
    </Modal>
  );
}

/* ==========================================================================
   الكشف: أربعُ خطواتٍ إلى الاعتماد
   ========================================================================== */

const STEPS = [
  { key: 'sales', title: 'إجمالي المبيعات', icon: 'receipt' },
  { key: 'collected', title: 'إجمالي المحصَّل', icon: 'wallet' },
  { key: 'reps', title: 'المندوبون وعمولاتهم', icon: 'users' },
  { key: 'approve', title: 'الاعتماد والإرسال', icon: 'check' },
] as const;

/**
 * الاعتمادُ قرارٌ يُبنى لا زرٌّ يُضغط.
 *
 * كانت الشاشةُ تعرض الجدولَ وزرَّ «اعتماد» بجانبه، فيُعتمد الكشفُ على
 * الثقة: لا يُسأل كم باعت الشركةُ هذا الشهر ولا كم حصّلت منه، ولا يُقرأ
 * نصيبُ كلِّ مندوبٍ من ذلك الكلّ. فصار الاعتمادُ أربعَ خطوات — ما بِيع،
 * ثمّ ما دخل، ثمّ من يستحقّ وبكم، ثمّ الاعتمادُ والإرسال — ومن مرّ بها
 * وصل إلى الزرّ وهو يعرف ما يوقّع عليه.
 *
 * والخطواتُ لا تُقفَل على من عاد ليقرأ: كشفٌ اعتُمد يُتصفَّح كما يُتصفَّح
 * المسوّدة، فما فيها قراءةٌ في الحالين.
 */
export function CommissionRunDetail() {
  const { atLeast } = useAuth();
  const canManage = atLeast('ADMIN');
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState<string | null>(null);
  const [step, setStep] = useState(0);
  const [slipFor, setSlipFor] = useState<Line | null>(null);
  const [runSlip, setRunSlip] = useState(false);

  const run = useQuery({
    queryKey: ['commissions', 'run', id],
    queryFn: () => api<RunDetail>(`/commissions/runs/${id}`),
  });

  const act = useMutation({
    mutationFn: (action: 'approve' | 'pay') =>
      api<Run & { payPeriod?: string }>(`/commissions/runs/${id}/${action}`, {
        method: 'POST',
      }),
    onSuccess: (data, action) => {
      void queryClient.invalidateQueries({ queryKey: ['commissions'] });
      if (action === 'approve') {
        setNotice(
          data.payPeriod
            ? `اعتُمد الكشف وظهر لكلّ مندوبٍ في «عمولاتي». تُصرف ضمن مرتب ${data.payPeriod}.`
            : 'اعتُمد الكشف وظهر لكلّ مندوبٍ في «عمولاتي».',
        );
      } else {
        setNotice('سُجِّل الصرف.');
      }
    },
  });

  const remove = useMutation({
    mutationFn: () => api(`/commissions/runs/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['commissions'] });
      window.location.href = '/sales/commissions';
    },
  });

  if (run.isError)
    return <ErrorState error={run.error} onRetry={() => run.refetch()} />;
  const d = run.data;

  const earners = d?.lines.filter((l) => l.commission > 0) ?? [];
  const t = d?.totals;
  /* نسبةُ التحصيل من المفوتر — الرقمُ الذي يقول هل دخل المالُ فعلًا. */
  const collectPct =
    t && t.invoiced > 0 ? Math.round((t.collected / t.invoiced) * 100) : 0;
  /* وحصّةُ العمولة من المحصَّل — كم يكلّف الشهرُ من المال الداخل. */
  const costPct =
    t && t.collected > 0 && d ? Math.round((d.total / t.collected) * 100) : 0;
  /* من قيس بهدف ملفّه لا بخطة شهره — يُقال قبل الاعتماد لا بعده. */
  const legacyTargets = d?.lines.filter((l) => l.targetSource === 'LEGACY') ?? [];

  return (
    <div className="page">
      <PageHead
        title={d ? `عمولات ${d.periodLabel}` : 'كشف عمولات'}
        subtitle={
          d
            ? `${plural(d.repCount, WORDS.rep)} · ${d.basisLabel} · ${d.targetPolicyLabel}`
            : undefined
        }
        actions={
          d && (
            <>
              <Link to="/sales/commissions" className="btn">
                <Icon name="arrowLeft" size={15} />
                الكشوف
              </Link>
              <button className="btn" onClick={() => setRunSlip(true)}>
                <Icon name="printer" size={15} />
                قسيمة الشهر
              </button>
              {canManage && d.status === 'DRAFT' && (
                <button
                  className="btn btn-danger"
                  onClick={() => remove.mutate()}
                  disabled={remove.isPending}
                >
                  حذف المسودة
                </button>
              )}
            </>
          )
        }
      />

      {act.isError && (
        <div className="alert alert-danger mb-4">
          {act.error instanceof Error ? act.error.message : 'تعذّرت العملية.'}
        </div>
      )}
      {notice && <div className="alert alert-success mb-4">{notice}</div>}

      {d && (
        <>
          {/* الخطواتُ شريطٌ يُنقر: كلُّ خطوةٍ بابٌ إلى ما تسأل عنه. */}
          <div className="wiz" role="tablist" aria-label="خطوات الاعتماد">
            {STEPS.map((s, i) => (
              <button
                key={s.key}
                type="button"
                role="tab"
                aria-selected={i === step}
                className={`wiz-step${i === step ? ' is-on' : ''}${
                  i < step || d.status !== 'DRAFT' ? ' is-done' : ''
                }`}
                onClick={() => setStep(i)}
              >
                <span className="wiz-num">
                  {i < step || d.status !== 'DRAFT' ? (
                    <Icon name="check" size={13} />
                  ) : (
                    i + 1
                  )}
                </span>
                <span className="wiz-body">
                  <b className="wiz-title">{s.title}</b>
                  <span className="wiz-note">
                    {i === 0 && t
                      ? `${money(t.invoiced)} · ${plural(t.invoiceCount, WORDS.invoice)}`
                      : i === 1 && t
                        ? `${money(t.collected)} · ${collectPct}٪ من المفوتر`
                        : i === 2
                          ? plural(earners.length, WORDS.earner)
                          : PAYROLL_RUN_STATUS_MAP[d.status].label}
                  </span>
                </span>
              </button>
            ))}
          </div>

          <div className="card mb-4">
            <div className="card-head">
              <h2 className="card-title">
                {step + 1}. {STEPS[step]!.title}
              </h2>
              <span className="tiny mute">{d.periodLabel}</span>
            </div>

            {step === 0 && t && (
              <div className="card-body">
                <div className="wiz-hero">
                  <b className="num wiz-hero-num">{money(t.invoiced)}</b>
                  <span className="wiz-hero-note">
                    من {plural(t.invoiceCount, WORDS.invoice)} صدرت في{' '}
                    {d.periodLabel} — عدا المسوّدات والملغاة.
                  </span>
                </div>
                <p className="tiny mute">
                  هذا ما باعته الشركةُ في الشهر كلِّه، لا ما يخصّ المندوبين
                  وحدهم: فاتورةٌ بلا مالكٍ تدخل في المبيعات ولا تدخل في عمولة
                  أحد، وإخفاؤها يجعل المجموعَ يكذب.
                </p>
              </div>
            )}

            {step === 1 && t && (
              <div className="card-body">
                <div className="wiz-hero">
                  <b className="num wiz-hero-num">{money(t.collected)}</b>
                  <span className="wiz-hero-note">
                    من {plural(t.paymentCount, WORDS.payment)} دخلت في{' '}
                    {d.periodLabel}.
                  </span>
                </div>
                <div className="row mb-2" style={{ gap: 'var(--sp-3)' }}>
                  <span className="bar-track" style={{ flex: 1 }}>
                    <span
                      className="tg-fill"
                      style={{
                        width: `${Math.min(100, collectPct)}%`,
                        ['--tg-pct' as string]: Math.max(
                          1,
                          Math.min(100, collectPct),
                        ),
                      }}
                    >
                      <span className="tg-grad" />
                    </span>
                  </span>
                  <b className="num">{collectPct}٪</b>
                </div>
                <p className="tiny mute">
                  {d.basis === 'COLLECTED'
                    ? 'العمولةُ في هذا الكشف تُحتسب على المحصَّل: ما لم يدخل الخزينةَ لا يُصرف عنه حافز.'
                    : 'العمولةُ في هذا الكشف تُحتسب على المفوتر لا على المحصَّل — يُصرف الحافزُ قبل دخول المال.'}
                </p>
              </div>
            )}

            {step === 2 && (
              <>
                {legacyTargets.length > 0 && (
                  <div className="card-body">
                    {/*
                     * الهدفُ من ملفٍ قديمٍ يُقال قبل الاعتماد لا بعده.
                     *
                     * سياسةُ الهدف تمنع العمولةَ أو تقصّها، فمن قيس برقمٍ
                     * لا شهرَ له قد يُحرَم حافزًا استحقّه أو يأخذ ما لم
                     * يستحقّ — والاعتمادُ لا رجعةَ فيه.
                     */}
                    <Notice kind="warning" title="أهدافٌ ليست من خطة السنة">
                      {plural(legacyTargets.length, WORDS.rep)} بلا خطةٍ في{' '}
                      {d.periodLabel}:{' '}
                      {legacyTargets.map((l) => l.employeeName).join('، ')}.
                      والهدفُ المقيس به مأخوذٌ من ملفّ الموظف — رقمٌ ثابتٌ لا
                      شهرَ له.{' '}
                      <Link to="/sales/team" className="link">
                        اضبط الخطة
                      </Link>{' '}
                      ثم احذف هذا الكشف وأعِد إنشاءه.
                    </Notice>
                  </div>
                )}
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>المندوب</th>
                        <th className="col-num">
                          {COMMISSION_BASIS_MAP[d.basis].amountLabel}
                        </th>
                        <th className="col-num">المستندات</th>
                        <th className="col-num">الهدف</th>
                        <th>التحقيق</th>
                        <th className="col-num">الخاضع</th>
                        <th className="col-num">النسبة</th>
                        <th className="col-num">العمولة</th>
                        <th className="col-act" />
                      </tr>
                    </thead>
                    <tbody>
                      {d.lines.map((l) => (
                        <tr key={l.id}>
                          <td>
                            <Link
                              to={`/sales/team/${l.employeeId}`}
                              className="cell-strong link"
                            >
                              {l.employeeName}
                            </Link>
                            {l.blockedReason && (
                              <div className="tiny mute">{l.blockedReason}</div>
                            )}
                          </td>
                          <td className="num">{money(l.basisAmount)}</td>
                          <td className="num cell-mute">{l.documentCount}</td>
                          <td className="num cell-mute">
                            {l.target == null ? '—' : money(l.target)}
                            {l.targetSource === 'LEGACY' && (
                              <div
                                className="tiny mute"
                                title={
                                  COMMISSION_TARGET_SOURCE_MAP.LEGACY.description
                                }
                              >
                                {COMMISSION_TARGET_SOURCE_MAP.LEGACY.label}
                              </div>
                            )}
                          </td>
                          <td>
                            <Achievement value={l.achievement} />
                          </td>
                          <td className="num cell-mute">{money(l.eligible)}</td>
                          <td className="num cell-mute">{num(l.rate)}%</td>
                          <td className="num cell-strong">
                            {money(l.commission)}
                          </td>
                          <td className="col-act">
                            <button
                              className="btn btn-sm"
                              onClick={() => setSlipFor(l)}
                              title="قسيمتُه — من أيّ مستنداتٍ وصفقاتٍ جاءت"
                            >
                              <Icon name="eye" size={13} />
                              معاينة
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={7} className="cell-strong">
                          الإجمالي
                        </td>
                        <td className="num cell-strong">{money(d.total)}</td>
                        <td />
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {d.lines.length === 0 && (
                  <EmptyState
                    title="لا مندوبين في هذا الكشف"
                    text="حدِّد الموظفين المعنيين بأنهم مندوبو مبيعات في ملفاتهم، ثم أعد الحساب."
                  />
                )}
              </>
            )}

            {step === 3 && t && (
              <div className="card-body">
                <div className="cz mb-4">
                  <MoneyTile
                    tone="var(--mv-1)"
                    icon="receipt"
                    label="إجمالي المبيعات"
                    value={money(t.invoiced)}
                    note={plural(t.invoiceCount, WORDS.invoice)}
                  />
                  <MoneyTile
                    tone="var(--mv-2)"
                    icon="wallet"
                    label="إجمالي المحصَّل"
                    value={money(t.collected)}
                    share={collectPct}
                    note={`${collectPct}٪ من المفوتر`}
                  />
                  <MoneyTile
                    tone="var(--brand)"
                    icon="users"
                    label="عمولات المندوبين"
                    value={money(d.total)}
                    share={costPct}
                    note={plural(earners.length, WORDS.earner)}
                  />
                  <MoneyTile
                    tone="var(--mv-3)"
                    icon="target"
                    label="كلفة العمولة"
                    value={`${costPct}٪`}
                    note="من المال الداخل هذا الشهر"
                  />
                </div>

                {d.status === 'DRAFT' ? (
                  <>
                    <div className="notices">
                      <Notice
                        kind="warning"
                        icon="alert"
                        title="الاعتمادُ لا رجعةَ فيه"
                      >
                        عند الاعتماد يُضاف لكلّ مستحقٍّ بندُ حافزٍ على مرتّبه،
                        ويظهر الكشفُ لكلّ مندوبٍ في صفحة «عمولاتي» — وبعدها لا
                        يُحذف الكشف.
                      </Notice>
                    </div>
                    {canManage ? (
                      <button
                        className="btn btn-primary btn-lg"
                        onClick={() => act.mutate('approve')}
                        disabled={act.isPending}
                      >
                        <Icon name="send" size={15} />
                        {act.isPending
                          ? 'جارٍ الاعتماد'
                          : `اعتماد وإرسال — ${money(d.total)}`}
                      </button>
                    ) : (
                      <p className="tiny mute">
                        الاعتمادُ لمن يملك صلاحيّة الإدارة.
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <div className="notices">
                      <Notice
                        kind="success"
                        icon="check"
                        /* «الكشفُ معتمدة» تخالف بين المذكّر والمؤنّث — فيُقال الفعل. */
                        title={
                          d.status === 'PAID'
                            ? 'صُرفت عمولات الشهر'
                            : 'اعتُمد الكشف وأُرسل'
                        }
                      >
                        {earners.length > 0
                          ? `أُضيف ${plural(earners.length, WORDS.bonusLine)} على المرتبات، ويراه كلُّ مندوبٍ في «عمولاتي».`
                          : 'لا مستحقّين في هذا الشهر.'}
                      </Notice>
                    </div>
                    <span className="row" style={{ gap: 'var(--sp-2)' }}>
                      <button className="btn" onClick={() => setRunSlip(true)}>
                        <Icon name="printer" size={14} />
                        قسيمة الشهر المجمّعة
                      </button>
                      {canManage && d.status === 'APPROVED' && (
                        <button
                          className="btn btn-primary"
                          onClick={() => act.mutate('pay')}
                          disabled={act.isPending}
                        >
                          <Icon name="check" size={14} />
                          تسجيل الصرف
                        </button>
                      )}
                    </span>
                  </>
                )}
              </div>
            )}
          </div>

          {/* خطوةٌ إلى الأمام وأخرى إلى الوراء — لا يُبحث عنهما في الشريط. */}
          <div className="row" style={{ gap: 'var(--sp-2)' }}>
            <button
              className="btn"
              onClick={() => setStep((x) => Math.max(0, x - 1))}
              disabled={step === 0}
            >
              <Icon name="chevronRight" size={14} />
              السابق
            </button>
            <button
              className="btn"
              onClick={() => setStep((x) => Math.min(STEPS.length - 1, x + 1))}
              disabled={step === STEPS.length - 1}
            >
              التالي
              <Icon name="chevronLeft" size={14} />
            </button>
          </div>

          {d.notes && <div className="alert alert-info mt-4">{d.notes}</div>}
        </>
      )}

      {slipFor && (
        <DocPreview
          path={`/print/commission-slip/${id}/${slipFor.employeeId}`}
          title={`قسيمة ${slipFor.employeeName}`}
          onClose={() => setSlipFor(null)}
        />
      )}

      {runSlip && d && (
        <DocPreview
          path={`/print/commission-run/${id}`}
          title={`قسيمة عمولات ${d.periodLabel}`}
          onClose={() => setRunSlip(false)}
        />
      )}
    </div>
  );
}

/* ==========================================================================
   عمولاتي
   ========================================================================== */

interface MyLine {
  id: string;
  runId: string;
  period: string;
  periodLabel: string;
  status: PayrollRunStatus;
  basisLabel: string;
  basisAmount: number;
  target: number | null;
  rate: number;
  eligible: number;
  commission: number;
  documentCount: number;
  paid: boolean;
}

interface MineData {
  year: number;
  years: number[];
  employee: {
    id: string;
    name: string;
    code: string | null;
    jobTitle: string | null;
    department: string | null;
    hireDate: string;
    isSalesRep: boolean;
    rate: number | null;
    /** هدفُ الشهر الجاري: من خطتي إن وُجدت، وإلّا من ملفّي. */
    target: number | null;
    targetSource: CommissionTargetSource;
    targetMonthLabel: string;
  } | null;
  totals: {
    earned: number;
    paid: number;
    waiting: number;
    months: number;
  } | null;
  lines: MyLine[];
}

/**
 * «عمولاتي» — ملفُّ المندوب عن نفسه.
 *
 * المديرُ يرى الفريقَ والكشوف، والمندوبُ لا يرى إلّا هذه. فهي صفحتُه
 * الوحيدة، ولا يليق أن تكون جدولًا عاريًا: فيها من هو، وكم استحقّ في سنته،
 * وكم صُرف، وشهرًا شهرًا بقسيمةٍ تُطبع — بالشكل نفسِه الذي يراه مديرُه في
 * ملفّه، فلا يختلف الرقمُ ولا هيئتُه بين الشاشتين.
 */
export function MyCommissions() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [slip, setSlip] = useState<MyLine | null>(null);

  const q = useQuery({
    queryKey: ['commissions', 'mine', year],
    queryFn: () => api<MineData>(`/commissions/mine${qs({ year })}`),
    placeholderData: (prev) => prev,
  });

  if (q.isError)
    return (
      <div className="page">
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      </div>
    );

  const me = q.data;
  const e = me?.employee;
  const t = me?.totals;
  const paidPct = t && t.earned > 0 ? Math.round((t.paid / t.earned) * 100) : 0;

  return (
    <div className="page">
      <PageHead
        title="عمولاتي"
        subtitle={
          e
            ? `${e.jobTitle ?? 'مندوب مبيعات'}${e.code ? ` · ${e.code}` : ''} · التحق في ${date(e.hireDate)}`
            : 'كشف عمولاتك المعتمدة'
        }
        actions={
          <YearSwitch year={year} onYear={setYear} max={now.getFullYear() + 1} />
        }
      />

      {me && !e && (
        <EmptyState
          title="لا ملف موظف مرتبط بحسابك"
          text="اطلب من الإدارة ربط حسابك بملفك في شؤون الموظفين ليظهر كشف عمولاتك."
        />
      )}

      {e && (
        <>
          <div className="two-col mb-4" style={{ alignItems: 'stretch' }}>
            <div className="card col-card">
              <div className="card-head">
                <h2 className="card-title">ملفّي</h2>
                <span className="tiny mute">من الموارد البشرية</span>
              </div>
              <div className="card-body col-body">
                <FactGrid>
                  <Fact
                    icon="briefcase"
                    label="المسمّى الوظيفي"
                    value={e.jobTitle || '—'}
                  />
                  <Fact
                    icon="building"
                    label="القسم"
                    value={e.department || '—'}
                  />
                  <Fact icon="clipboard" label="الكود" value={e.code || '—'} ltr />
                  <Fact
                    icon="calendar"
                    label="تاريخ الالتحاق"
                    value={date(e.hireDate)}
                  />
                  <Fact
                    icon="target"
                    label="نسبتي"
                    value={e.rate == null ? 'حسب الإعدادات' : `${num(e.rate)}٪`}
                  />
                  {/*
                   * «هدفي» هدفُ شهرٍ بعينه لا رقمٌ مطلق: البطاقةُ تسمّي شهرَه
                   * وإلّا قرأه المندوبُ هدفًا للسنة أو للسنة الماضية المعروضة.
                   */}
                  <Fact
                    icon="zap"
                    label={`هدفي في ${e.targetMonthLabel}`}
                    value={e.target == null ? 'بلا هدف' : money(e.target)}
                  />
                </FactGrid>
              </div>
            </div>

            <div className="card col-card">
              <div className="card-head">
                <h2 className="card-title">ما صُرف من {year}</h2>
                <span className="tiny mute">من عمولات السنة</span>
              </div>
              <div className="card-body col-body">
                <div className="ach">
                  <AchieveRing
                    pct={paidPct}
                    caption="صُرف"
                    empty={t && t.earned > 0 ? undefined : 'لا عمولات'}
                  />
                  <div className="ach-side">
                    <div className="ach-row">
                      <span className="pf-k">مستحقٌّ في {year}</span>
                      <b className="num ach-val">{money(t?.earned ?? 0)}</b>
                    </div>
                    <div className="ach-row">
                      <span className="pf-k">صُرف</span>
                      <b className="num ach-val is-mute">{money(t?.paid ?? 0)}</b>
                    </div>
                    <div className="ach-row">
                      <span className="pf-k">ينتظر الصرف</span>
                      <b className="num ach-val is-mute">
                        {money(t?.waiting ?? 0)}
                      </b>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-head">
              <h2 className="card-title">كشوف {year}</h2>
              <span className="tiny mute">
                ثلاثةُ شهورٍ في النظرة، والباقي بالتمرير
              </span>
            </div>

            <div className="card-body">
              <div className="cz">
                <MoneyTile
                  tone="var(--brand)"
                  icon="wallet"
                  label={`عمولات ${year}`}
                  value={money(t?.earned ?? 0)}
                  share={paidPct}
                  note={`${num(t?.months ?? 0)} كشفًا معتمدًا`}
                />
                <MoneyTile
                  tone="var(--success)"
                  icon="check"
                  label="صُرف مع المرتّب"
                  value={money(t?.paid ?? 0)}
                  share={paidPct}
                  note={`${paidPct}٪ من المستحقّ`}
                />
                <MoneyTile
                  tone="var(--warning)"
                  icon="clock"
                  label="ينتظر الصرف"
                  value={money(t?.waiting ?? 0)}
                  share={100 - paidPct}
                  note="اعتُمد ولم ينزل بعد"
                />
                <MoneyTile
                  tone="var(--mv-3)"
                  icon="target"
                  label="نسبتي"
                  value={e.rate == null ? '—' : `${num(e.rate)}٪`}
                  note={
                    e.rate == null
                      ? 'تُؤخذ من الإعدادات'
                      : 'تُطبَّق على الخاضع للعمولة'
                  }
                />
              </div>
            </div>

            {me!.lines.length > 0 ? (
              <div
                className="table-wrap sticky-head rows-cap"
                style={{ ['--rows' as string]: 3, ['--rowh' as string]: '68px' }}
              >
                <table className="table">
                  <thead>
                    <tr>
                      <th>الشهر</th>
                      <th>الأساس</th>
                      <th className="col-num">ما احتُسب عليه</th>
                      <th className="col-num">الخاضع</th>
                      <th className="col-num">النسبة</th>
                      <th className="col-num">العمولة</th>
                      <th className="col-num">المستندات</th>
                      <th>الحالة</th>
                      <th className="col-act" />
                    </tr>
                  </thead>
                  <tbody>
                    {me!.lines.map((l) => (
                      <tr key={l.id}>
                        <td className="cell-strong">{l.periodLabel}</td>
                        <td className="cell-mute">{l.basisLabel}</td>
                        <td className="num cell-mute">{money(l.basisAmount)}</td>
                        <td className="num">{money(l.eligible)}</td>
                        <td className="num cell-mute">{num(l.rate)}٪</td>
                        <td className="num cell-strong">
                          {money(l.commission)}
                        </td>
                        <td className="num cell-mute">{num(l.documentCount)}</td>
                        <td>
                          <Badge kind={PAYROLL_RUN_STATUS_MAP[l.status].kind}>
                            {PAYROLL_RUN_STATUS_MAP[l.status].label}
                          </Badge>
                        </td>
                        <td className="col-act">
                          <button
                            className="btn btn-sm"
                            onClick={() => setSlip(l)}
                            title="قسيمتي عن هذا الشهر — تُطبع أو تُحفظ"
                          >
                            <Icon name="eye" size={13} />
                            معاينة
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <EmptyState
                title={`لا عمولات معتمدة في ${year}`}
                text="يظهر هنا كشف كل شهر بعد أن تعتمده الإدارة."
              />
            )}
          </div>

          {me!.years.length > 0 && (
            <p className="tiny mute">
              لك كشوفٌ في: {me!.years.map((y) => String(y)).join('، ')}.
            </p>
          )}
        </>
      )}

      {/*
        القسيمةُ مستندٌ يُطبع لا نافذةٌ تُقرأ.
        و«me» بدل معرّف الموظف: الخادمُ يشتقّه من الجلسة، فلا يقرأ أحدٌ
        قسيمةَ غيره بتبديل رقمٍ في المسار.
      */}
      {slip && (
        <DocPreview
          path={`/print/commission-slip/${slip.runId}/me`}
          title={`قسيمة عمولة — ${slip.periodLabel}`}
          onClose={() => setSlip(null)}
        />
      )}
    </div>
  );
}
